import Reveal from '@/components/feature/Reveal';
import { formatBalance, formatSigned, formatTradeDate } from '@/utils/numberFormat';
import type { TradeEntry } from '@/utils/performanceSource';

const RECENT_COUNT = 12;

interface RecentTradesProps {
  trades: TradeEntry[];
  /** Opening capital the running account total is measured from (deposit − withdrawal). */
  startingCapital: number;
}

interface RecentRow {
  date: string;
  pnl: number;
  cumulative: number;
}

/** Length + direction of the streak that ends on the most recent trade. */
function currentStreak(entries: TradeEntry[]): { wins: boolean; length: number } {
  if (entries.length === 0) return { wins: true, length: 0 };
  const lastWon = entries[entries.length - 1][1] >= 0;
  let length = 0;
  for (let i = entries.length - 1; i >= 0; i -= 1) {
    const won = entries[i][1] >= 0;
    if (won === lastWon) length += 1;
    else break;
  }
  return { wins: lastWon, length };
}

export default function RecentTrades({ trades, startingCapital }: RecentTradesProps) {
  const entries = trades;
  const total = entries.length;
  const wins = entries.filter(([, pnl]) => pnl >= 0).length;
  const winRate = total > 0 ? (wins / total) * 100 : 0;
  const streak = currentStreak(entries);

  /* True running account balance (opening capital + P/L) after each fill, newest first. */
  let running = startingCapital;
  const recent: RecentRow[] = entries
    .map(([date, pnl]) => {
      running += pnl;
      return { date, pnl, cumulative: running };
    })
    .slice(-RECENT_COUNT)
    .reverse();

  return (
    <Reveal>
      <div id="trades" className="mt-12 border-t border-background-200 pt-10 scroll-mt-32 md:scroll-mt-36">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">
              Recent Closed Trades
            </h2>
            <p className="mt-2 text-sm text-foreground-600 max-w-xl">
              The latest closed positions from the live account, newest first — with the running
              account balance after each fill (opening capital plus cumulative P/L) so you can watch
              it move trade by trade.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-secondary-100 px-3 py-1.5 text-[11px] md:text-xs font-semibold text-secondary-900">
              <i className="ri-history-line" aria-hidden="true"></i>
              {wins}W · {winRate.toFixed(0)}% · {total} trades
            </span>
            {streak.length > 0 ? (
              <span
                className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[11px] md:text-xs font-semibold ${
                  streak.wins ? 'bg-primary-100 text-primary-700' : 'bg-accent-100 text-accent-900'
                }`}
              >
                <i className={streak.wins ? 'ri-fire-line' : 'ri-snowy-line'} aria-hidden="true"></i>
                {streak.length} {streak.wins ? 'W' : 'L'} streak
              </span>
            ) : null}
          </div>
        </div>

        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5 sm:gap-3">
          {recent.map((row, index) => {
            const isWin = row.pnl >= 0;
            return (
              <div
                key={`${row.date}-${index}`}
                className={`flex flex-col rounded-lg border p-3 sm:p-3.5 ${
                  isWin ? 'border-primary-200 bg-primary-50/60' : 'border-accent-200 bg-accent-50/60'
                }`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="text-[11px] sm:text-xs font-medium text-foreground-500">
                    {formatTradeDate(row.date)}
                  </span>
                  <span
                    className={`inline-flex items-center gap-1 text-[11px] font-semibold ${
                      isWin ? 'text-primary-700' : 'text-accent-900'
                    }`}
                  >
                    <i className={isWin ? 'ri-arrow-up-line' : 'ri-arrow-down-line'} aria-hidden="true"></i>
                    {isWin ? 'Win' : 'Loss'}
                  </span>
                </div>
                <p
                  className={`mt-1.5 sm:mt-2 font-num text-base sm:text-lg font-bold ${
                    isWin ? 'text-primary-600' : 'text-secondary-500'
                  }`}
                >
                  {formatSigned(row.pnl)}
                  <span className="ml-1 text-[10px] font-normal text-foreground-400">USC</span>
                </p>
                <div className="mt-2 sm:mt-3 flex items-center justify-between gap-2 border-t border-background-200/70 pt-2">
                  <span className="text-[10px] uppercase tracking-wide text-foreground-400">
                    Account total
                  </span>
                  <span
                    className={`font-num text-[11px] sm:text-xs font-semibold ${
                      row.cumulative >= startingCapital ? 'text-foreground-800' : 'text-secondary-500'
                    }`}
                  >
                    {formatBalance(row.cumulative)}
                  </span>
                </div>
              </div>
            );
          })}
        </div>

        <p className="mt-4 text-xs text-foreground-500">
          Showing the {Math.min(RECENT_COUNT, total)} most recent of {total} closed trades. The complete MT5
          statement is available on request in the support chat.
        </p>
      </div>
    </Reveal>
  );
}